import type { i18n as I18nInstance } from 'i18next';
import { PropsWithChildren, useEffect, useState } from 'react';
import { I18nextProvider } from 'react-i18next';

import { CustomLoadingOverlay } from '@hendrx/shared/components/app/CustomLoadingOverlay';
import { SupportedLocale } from 'shared/common/types/SupportedLocale';
import { useFetchTranslationsForLocale } from 'shared/queries/translation';

type IntlLocaleProviderProps = PropsWithChildren<{
  i18nInstance: I18nInstance;
  locale: SupportedLocale;
}>;

function createNamespaces(
  data: Record<string, any>,
  locale: string,
  i18nInstance: I18nInstance,
  prefix: string[] = []
): void {
  for (const [key, value] of Object.entries(data)) {
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      const currentPath = [...prefix, key];
      const namespaceName = currentPath.join('.');

      i18nInstance.addResourceBundle(locale, namespaceName, value, true, true);

      createNamespaces(data[key], locale, i18nInstance, currentPath);
    }
  }
}

export function IntlLocaleProvider({ children, locale, i18nInstance }: IntlLocaleProviderProps) {
  const { data, isLoading } = useFetchTranslationsForLocale(locale);

  const [loadedLocale, setLoadedLocale] = useState<string | null>(() =>
    i18nInstance.hasResourceBundle(locale, 'translation') ? locale : null
  );

  useEffect(() => {
    let isActive = true;
    const loadTranslations = async () => {
      if (data && !isLoading) {
        if (i18nInstance.language !== locale) {
          await i18nInstance.changeLanguage(locale);
        }
        if (!isActive) return;
        createNamespaces(data, locale, i18nInstance);
        for (const [key, value] of Object.entries(data)) {
          if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            i18nInstance.addResourceBundle(locale, key, value, true, true);
          }
        }
        i18nInstance.addResourceBundle(locale, 'translation', data, true, true);
        if (isActive) {
          setLoadedLocale(locale);
        }
      } else if (isActive) {
        setLoadedLocale(null);
      }
    };
    void loadTranslations();
    return () => {
      isActive = false;
    };
  }, [data, locale, isLoading, i18nInstance]);

  if (isLoading || (data && loadedLocale !== locale)) {
    return <CustomLoadingOverlay visible />;
  }

  return <I18nextProvider i18n={i18nInstance}>{children}</I18nextProvider>;
}
